import { ChakraProvider, useColorMode } from "@chakra-ui/react";
import { createContext, useContext, useEffect, useState } from "react";

import { useSystemTheme } from "hooks/useSystemTheme";
import type { ThemePreference } from "util/theme";
import { getSavedThemePreference, saveThemePreference } from "util/theme";

import theme from "../chakraTheme";

interface ThemeContextType {
  themePreference: ThemePreference;
  setTheme: (theme: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useThemeManager = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useThemeManager must be used within a ThemeProvider");
  }
  return context;
};

interface ThemeProviderProps {
  children: React.ReactNode;
}

const ThemeManager = ({ children }: { children: React.ReactNode }) => {
  const { setColorMode } = useColorMode();
  const systemTheme = useSystemTheme();
  const [themePreference, setThemePreference] =
    useState<ThemePreference>("system");

  // Load initial theme preference
  useEffect(() => {
    const savedPreference = getSavedThemePreference();
    if (savedPreference) {
      setThemePreference(savedPreference);
    }
  }, []);

  // Handle theme changes
  useEffect(() => {
    const actualTheme =
      themePreference === "system" ? systemTheme : themePreference;
    setColorMode(actualTheme);
  }, [themePreference, systemTheme, setColorMode]);

  const setTheme = (theme: ThemePreference) => {
    setThemePreference(theme);
    saveThemePreference(theme);
  };

  return (
    <ThemeContext.Provider value={{ themePreference, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const ThemeProvider = ({ children }: ThemeProviderProps) => {
  return (
    <ChakraProvider theme={theme}>
      <ThemeManager>{children}</ThemeManager>
    </ChakraProvider>
  );
};
